import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    Box,
    IconButton,
    Paper
} from '@mui/material';
import {
    Close as CloseIcon,
    Assessment as DocIcon,
    BarChart as ChartIcon,
    Check as CheckIcon,
    ArrowForwardIos as ArrowIcon
} from '@mui/icons-material';
import { COLORS } from '../../../settings/themeSettings';

export const ANALYSES_LIST = [
    { id: 'doc1', title: 'Analyse globale par activité', code: 'Activité', description: 'Chiffre d\'affaires, Débours/Achats, Marges Billetterie, Tourisme et Global' },
    { id: 'doc2', title: 'Analyse Chiffre d\'affaires et marges', code: 'Marges & CA', description: 'Structure en % du Chiffre d\'affaires et de la Marge par activité' },
    { id: 'doc3', title: 'Analyse billetterie par compagnie', code: 'Billetterie', description: 'Synthèse billetterie et ventilation détaillée par compagnie aérienne' },
    { id: 'doc4', title: 'Analyse tourisme par prestation', code: 'Tourisme', description: 'Synthèse tourisme et ventilation par prestation (Circuits, Excursions, Transferts, Séjours)' },
    { id: 'doc5', title: 'Analyse tourisme client', code: 'Clients', description: 'Synthèse tourisme et ventilation du CA par client / agence partenaire' },
    { id: 'graphique', title: 'Analyse graphique', code: 'Graphiques', description: 'Histogrammes comparatifs et diagrammes de répartition' },
];

const DashboardAnalysisModal = ({
    open,
    onClose,
    onSelectAnalysis,
    initialAnalysisId = 'doc1'
}) => {
    const [selectedId, setSelectedId] = useState(initialAnalysisId);

    useEffect(() => {
        if (initialAnalysisId) setSelectedId(initialAnalysisId);
    }, [initialAnalysisId]);

    // Navigation au clavier dans la liste (flèches haut/bas et entrée)
    const handleKeyDown = (e) => {
        const currentIndex = ANALYSES_LIST.findIndex(a => a.id === selectedId);
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            const nextIndex = (currentIndex + 1) % ANALYSES_LIST.length;
            setSelectedId(ANALYSES_LIST[nextIndex].id);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            const prevIndex = (currentIndex - 1 + ANALYSES_LIST.length) % ANALYSES_LIST.length;
            setSelectedId(ANALYSES_LIST[prevIndex].id);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            onSelectAnalysis(selectedId);
        }
    };

    const handleConfirm = () => {
        onSelectAnalysis(selectedId);
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="sm" 
            fullWidth
            onKeyDown={handleKeyDown}
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
                }
            }}
        >
            <DialogTitle sx={{ 
                bgcolor: COLORS.secondary || '#0f172a', 
                color: 'white', 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                py: 1.5
            }}>
                <Box>
                    <Typography variant="h6" fontWeight="bold" component="div">
                        Sélection du tableau de bord
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                        Sélectionnez le document ou le graphique à consulter
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 2 }}>
                <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
                    <List disablePadding>
                        {ANALYSES_LIST.map((item, index) => {
                            const isSelected = selectedId === item.id;
                            const isChart = item.id === 'graphique';

                            return (
                                <ListItemButton
                                    key={item.id}
                                    selected={isSelected}
                                    onClick={() => {
                                        setSelectedId(item.id);
                                        // Point particulier : Le clic sur une ligne affiche directement le document
                                        onSelectAnalysis(item.id);
                                    }}
                                    sx={{
                                        py: 1.5,
                                        px: 2,
                                        borderBottom: index < ANALYSES_LIST.length - 1 ? '1px solid #e2e8f0' : 'none',
                                        bgcolor: isSelected ? '#edf3fc !important' : 'transparent',
                                        borderLeft: isSelected ? `4px solid ${COLORS.primary || '#0ea5e9'} !important` : '4px solid transparent',
                                        '&:hover': {
                                            bgcolor: '#f1f5f9',
                                        },
                                        transition: 'all 0.15s ease'
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 40, color: isSelected ? (COLORS.primary || '#0ea5e9') : '#64748b' }}>
                                        {isChart ? <ChartIcon /> : <DocIcon />}
                                    </ListItemIcon>
                                    
                                    <ListItemText 
                                        primary={
                                            <Typography variant="subtitle1" fontWeight={isSelected ? 700 : 500} color={isSelected ? 'primary.main' : 'text.primary'}>
                                                {item.title}
                                            </Typography>
                                        }
                                        secondary={item.description}
                                        secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                                    />

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="caption" sx={{ 
                                            bgcolor: isSelected ? alpha(COLORS.primary || '#0ea5e9', 0.15) : '#f1f5f9',
                                            color: isSelected ? 'primary.main' : 'text.secondary',
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: 1,
                                            fontWeight: 'bold',
                                            fontSize: '0.75rem'
                                        }}>
                                            {item.code}
                                        </Typography>
                                        <ArrowIcon fontSize="small" sx={{ color: isSelected ? 'primary.main' : '#cbd5e1' }} />
                                    </Box>
                                </ListItemButton>
                            );
                        })}
                    </List>
                </Paper>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
                <Button 
                    variant="outlined" 
                    color="inherit" 
                    onClick={onClose}
                    sx={{ textTransform: 'none' }}
                >
                    Annuler
                </Button>
                <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<CheckIcon />}
                    onClick={handleConfirm}
                    sx={{ textTransform: 'none', fontWeight: 'bold', px: 3 }}
                >
                    OK (Afficher)
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// Helper alpha pour MUI
const alpha = (color, opacity) => {
    return color ? `${color}${Math.round(opacity * 255).toString(16).padStart(2, '0')}` : `rgba(14, 165, 233, ${opacity})`;
};

export default DashboardAnalysisModal;
